import { useLocation, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { isActiveLink } from "../utils/linkActiveChecker";


type NavItem = {
  to: string;
  icon: string;
  label: string;
};

type NavSection = {
  id: string;
  label: string;
  icon: string;
  items: NavItem[];
};

const navSections: NavSection[] = [
  {
    id: "overview",
    label: "Overview",
    icon: "fa-chart-pie",
    items: [{ to: "/admin/dashboard", icon: "fa-home", label: "Dashboard" }],
  },
  {
    id: "clients",
    label: "Clients",
    icon: "fa-users",
    items: [{ to: "/admin/list-clients", icon: "fa-user-friends", label: "Clients List" }],
  },
  {
    id: "visa",
    label: "Visa Services",
    icon: "fa-passport",
    items: [
      { to: "/admin/list-request-visas", icon: "fa-file-alt", label: "Visa Requests" },
      { to: "/admin/list-packs", icon: "fa-box", label: "Visa Packs" },
    ],
  },
  {
    id: "tours",
    label: "Tours & Travel",
    icon: "fa-plane-departure",
    items: [
      { to: "/admin/list-trips", icon: "fa-route", label: "Tours" },
      { to: "/admin/list-activities", icon: "fa-compass", label: "Activities" },
      { to: "/admin/list-booking-tour", icon: "fa-calendar-alt", label: "Bookings tour" },
      { to: "/admin/list-booking-activites", icon: "fa-calendar-alt", label: "Bookings activites" },
    ],
  },
  {
    id: "flights",
    label: "Flights",
    icon: "fa-plane",
    items: [
      { to: "/admin/list-flight-requests", icon: "fa-plane-departure", label: "Flight Requests" },
      { to: "/admin/list-dummy-tickets", icon: "fa-ticket", label: "Dummy Tickets" },
    ],
  },
  {
    id: "cars",
    label: "Car Rental",
    icon: "fa-car",
    items: [
      { to: "/admin/list-cars", icon: "fa-car-side", label: "Fleet" },
      { to: "/admin/list-request-rent-car", icon: "fa-taxi", label: "Rental Requests" },
      { to: "/admin/calendar-booking-cars", icon: "fa-taxi", label: "Calendar Rental Requests" },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: "fa-cogs",
    items: [
      { to: "/admin/list-ticket", icon: "fa-ticket-alt", label: "Tickets" },
      { to: "/admin/list-invoices", icon: "fa-file-invoice-dollar", label: "Invoices" },
      { to: "/admin/list-blog", icon: "fa-newspaper", label: "Blog" },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: "fa-cog",
    items: [
      { to: "/admin/list-countries", icon: "fa-globe-americas", label: "Countries" },
    ],
  },

];

interface SidebarAdminProps {
  collapsed?: boolean;
}

const SidebarAdmin = ({ collapsed = false }: SidebarAdminProps) => {
  const location = useLocation();
  const pathname = location.pathname;

  // State pour suivre les accordéons ouverts
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  // Auto-ouvrir la section où se trouve la page active
  useEffect(() => {
    navSections.forEach((section) => {
      const hasActiveChild = section.items.some((item) => isActiveLink(pathname, item.to));
      if (hasActiveChild) {
        setOpenSections((prev) => ({ ...prev, [section.id]: true }));
      }
    });
  }, [pathname]);

  const toggleSection = (id: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <div className={`sidebar -dashboard ${collapsed ? "is-collapsed" : ""}`}>
      {/* Brand Header */}
      <div className="sidebar__logo">
        <Link to="/admin/dashboard">
          <img
            src={collapsed ? "/img/logo_mark_white.png" : "/img/logo_white.png"}
            alt="Silverjarz"
            className="sidebar-logo"
          />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="sidebar__nav custom-scrollbar">
        {navSections.map((section) => {
          const isOpen = !!openSections[section.id];
          const hasActiveChild = section.items.some((item) => isActiveLink(pathname, item.to));

          return (
            <div
              key={section.id}
              className={`sidebar__accordion ${isOpen ? "is-open" : ""} ${
                hasActiveChild ? "has-active" : ""
              }`}
            >
              {/* Entête de Section (Cliquable) */}
              <button
                className="sidebar__accordion-header"
                onClick={() => toggleSection(section.id)}
                title={collapsed ? section.label : undefined}
              >
                <div className="d-flex align-items-center gap-2">
                  <i className={`fas ${section.icon} sidebar__icon`}></i>
                  {!collapsed && <span className="sidebar__section-title">{section.label}</span>}
                </div>
                {!collapsed && (
                  <i
                    className={`fas fa-chevron-right sidebar__chevron ${isOpen ? "rotated" : ""}`}
                  ></i>
                )}
              </button>

              {/* Sous-Menu (Items) */}
              {(isOpen || collapsed) && (
                <div className="sidebar__accordion-body">
                  {section.items.map((item) => {
                    const active = isActiveLink(pathname, item.to);
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`sidebar__link ${active ? "is-active" : ""}`}
                        title={collapsed ? item.label : undefined}
                      >
                        <span className="sidebar__link-bar" />
                        <i className={`fas ${item.icon} sidebar__sub-icon`}></i>
                        {!collapsed && <span className="sidebar__label">{item.label}</span>}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
};

export default SidebarAdmin;